import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styles from '../../css/studyIndexCSS.less';
import {
  ContentTypes,
  ExercisesType,
  ExercisesItemType,
  ExercisesConfigType,
} from '@/types/camp/study/contentTypes';
import { QuestionType } from '@/types/camp/study/index';
import { Form, Radio, Checkbox, Input, Row, Col } from 'antd';
import Editor from '@digit-fe/digit-editor-new';
import { debounce } from 'lodash';
import { editParseJson } from '@/utils/index';
import { onFileAndVideoUpload, onImageUpload } from '@/pages/camp/components/editor/uploadUtil';

interface Props {
  item: ContentTypes;
  handleAddItemOrUpdate: (objArr: ContentTypes) => void;
  form: any;
}
const QuestionNodeFail: React.FC<Props> = ({ item, handleAddItemOrUpdate, form }) => {
  const info = item.info as ExercisesType;
  const [questions, setQuestions] = useState<ExercisesItemType[]>(info.question);
  const [exercises, setExercises] = useState<ExercisesConfigType[]>(info.exercises || []);
  const editDataRef = useRef<any>('');
  const editUploadRef = useRef(false);

  // 在组件加载时设置表单的初始值
  React.useEffect(() => {
    let initialData: any = {};
    questions.forEach((item) => {
      initialData[item.id] = item.studentAnswer;
    });
    form.setFieldsValue(initialData);
  }, []);

  let debouncedUpdate = debounce((id: string, data: any) => {
    if (!editUploadRef.current) {
      let newData = JSON.stringify(data);
      // console.log('newData', data);
      updateQuestion(id, newData);
      handleEditorChange(data, id);
    }
  }, 1000);

  const handleEditorChange = (value: any, id: any) => {
    // console.log('value.content[0].content ', value.content[0].content);
    // console.log('value.content[0].attrs ', value.content[0].attrs.url);
    form.setFieldsValue({ [id]: value.content[0].content || value.content[0].attrs.url });
  };

  const validateContent = (rule: any, value: any) => {
    if (value === undefined) {
      return Promise.reject(new Error('Content is required'));
    }
    return Promise.resolve();
  };

  // 处理显示对应表单类型
  const handleFormItem = (item: ExercisesItemType, index: number) => {
    switch (item.type) {
      // 单选题
      case QuestionType.SINGLE_CHOICE:
        if (item.info && typeof item.info === 'object') {
          return (
            <div className="my_ant_form_item" id={`${item.id}_title`}>
              <div className="form_tag">单选题</div>
              <Form.Item
                key={item.id}
                name={item.id}
                label={item.title}
                rules={[{ required: true }]}
              >
                <Radio.Group
                  value={form.getFieldValue(item.id)}
                  onChange={(e) => {
                    updateQuestion(item.id, e.target.value);
                  }}
                >
                  {item.description && (
                    <Editor
                      style={{ backgroundColor: '#F7FAFD' }}
                      content={item.description && JSON.parse(item.description)}
                      editable={false}
                      titleProps={{ showTitle: false }}
                      catalogProps={{ showCatalog: false }}
                    />
                  )}
                  {item.info?.options?.map((o, index) => (
                    <Radio key={o.index} value={o.description}>
                      {`${String.fromCharCode(65 + index)}. ${o.description}`}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </div>
          );
        }
        break;
      // 判断题
      case QuestionType.TURE_OR_FALSE:
        if (item.info && typeof item.info === 'object') {
          return (
            <div className="my_ant_form_item" id={`${item.id}_title`}>
              <div className="form_tag">判断题</div>
              <Form.Item
                key={item.id}
                name={item.id}
                label={item.title}
                rules={[{ required: true }]}
              >
                <Radio.Group onChange={(e) => updateQuestion(item.id, e.target.value)}>
                  {item.description && Object.keys(item.description).length !== 0 && (
                    <Editor
                      style={{ backgroundColor: '#F7FAFD' }}
                      content={item.description && JSON.parse(item.description)}
                      editable={false}
                      titleProps={{ showTitle: false }}
                      catalogProps={{ showCatalog: false }}
                    />
                  )}
                  <Radio value="true">A. 正确</Radio>
                  <Radio value="false">B. 错误</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          );
        }
        break;
      // 主观题
      case QuestionType.SUBJECTIVE:
        return (
          <div className="my_ant_form_item" id={`${item.id}_title`}>
            <div className="form_tag">主观题</div>
            <Form.Item
              key={item.id}
              label={item.title}
              name={item.id}
              rules={[{ required: true }, { validator: validateContent }]}
            >
              {item.description && (
                <Editor
                  key={`edit_description_${index}`}
                  style={{ backgroundColor: '#F7FAFD' }}
                  content={item.description && JSON.parse(item.description)}
                  editable={false}
                  titleProps={{ showTitle: false }}
                  catalogProps={{ showCatalog: false }}
                />
              )}
            </Form.Item>
          </div>
        );

        break;
      // 多选题
      case QuestionType.MULTIPLE_CHOICE:
        if (item.info && typeof item.info === 'object') {
          return (
            <div className="my_ant_form_item" id={`${item.id}_title`}>
              <div className="form_tag">多选题</div>
              <Form.Item
                key={item.id}
                name={item.id}
                label={item.title}
                rules={[{ required: true }]}
              >
                <Checkbox.Group onChange={(e) => updateQuestion(item.id, e)}>
                  {item.description && Object.keys(item.description).length !== 0 && (
                    <Editor
                      style={{ backgroundColor: '#F7FAFD' }}
                      content={item.description && JSON.parse(item.description)}
                      editable={false}
                      titleProps={{ showTitle: false }}
                      catalogProps={{ showCatalog: false }}
                    />
                  )}
                  {item.info?.options?.map((o, k) => (
                    <Checkbox key={k} value={o.description}>
                      {`${String.fromCharCode(65 + k)}. ${o.description}`}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
            </div>
          );
        }
        break;
      default:
        return <Input hidden key={`inputHidden_${index}`} />;
    }
  };

  const updateQuestion = useCallback((id: string, newValue: any) => {
    console.log('newValue', newValue);

    setQuestions((prev) =>
      prev.map((q) =>
        q.id === id
          ? {
              ...q,
              studentAnswer: typeof newValue === 'string' ? newValue : JSON.stringify(newValue),
            }
          : q,
      ),
    );
  }, []);

  useEffect(() => {
    const newdata: ContentTypes = {
      id: item.id,
      index: item.index,
      info: {
        question: questions,
      },
      name: item.name,
      nodeId: item.nodeId,
      type: item.type,
    };
    // console.log('questions:', questions);
    // console.log('newdata', newdata);
    handleAddItemOrUpdate(newdata);
  }, [questions]);

  const renderedQuestions = useMemo(() => {
    return questions.map((item, index) => (
      <div>
        {item.categoryName && exercises.length >= 2 && (
          <div className="f20" style={{ padding: '16px 0 16px' }}>
            {item.categoryName}（每题{item.rewardScore}分）
          </div>
        )}
        <div>{handleFormItem(item, index)}</div>
      </div>
    ));
  }, []);

  return (
    <div className={styles.questions_main}>
      <div className={styles.new_form_item}>
        {/* 习题只有一个分组 */}
        {exercises.length < 2 && (
          <div className="f20" style={{ marginBottom: 16 }}>
            <div>
              {exercises[0].title}（每题{exercises[0].rewardPer}分）
            </div>
          </div>
        )}
        {/* 习题多个分组 */}
        <div className="com_edit_max_width">{renderedQuestions}</div>
      </div>
    </div>
  );
};
export default QuestionNodeFail;
